/**
 * Copied heavily from: https://github.com/vespaiach/axios-fetch-adapter and modified to make compatible to Axios v1 instead of v0
 * Accessed: 2023-12-13
 */

import { getCloudflareCompatibleOptions } from "@shared/utils/api";
import {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
// @ts-ignore - this is an internal axios function
import buildFullPath from "axios/unsafe/core/buildFullPath.js";
// @ts-ignore - this is an internal axios function
import buildURL from "axios/unsafe/helpers/buildURL.js";

type FetchAPIRequest = Request;
type AdapterFunction = (config: AxiosRequestConfig) => Promise<AxiosResponse>;

export function createFetchAdapter(): AdapterFunction {
  async function axiosAdapter(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    const fetchRequest = createRequest(config);
    const responsePromise = getResponsePromise(fetchRequest, config);

    let response: AxiosResponse | AxiosError;

    const { timeoutPromise, timer } = getTimeoutPromise(config, fetchRequest);

    if (timeoutPromise) {
      response = await Promise.race([responsePromise, timeoutPromise]);
      clearTimeout(timer);
    } else {
      response = await responsePromise;
    }

    return new Promise((resolve, reject) => {
      if (response instanceof Error) {
        reject(response);
      } else {
        const validateStatus = config.validateStatus;
        const shouldReject = validateStatus && !validateStatus(response.status);

        if (shouldReject) {
          reject(
            createError(
              `Request failed with status code ${response.status}`,
              config,
              getErrorCodeFromStatus(response.status),
              fetchRequest,
              response,
            ),
          );
        } else {
          resolve(response);
        }
      }
    });
  }

  return axiosAdapter;
}

/**
 * An Axios adapter for the Fetch API.
 *
 * The adapter creates and executes the request with the given Axios config using the Fetch API, and returns an Axios response promise.
 * This is useful for environments where you can't use XHR or HTTP, such as the Cloudflare workers where we make deployments in SSR.
 */
const axiosFetchAdapter = createFetchAdapter();

export default axiosFetchAdapter;

function getTimeoutPromise(
  config: AxiosRequestConfig,
  request: Request,
): {
  timeoutPromise: Promise<AxiosError> | undefined;
  timer: NodeJS.Timeout | undefined;
} {
  const timeout = config.timeout;

  if (!timeout || timeout <= 0)
    return {
      timeoutPromise: undefined,
      timer: undefined,
    };

  let timer: NodeJS.Timeout | undefined;

  return {
    timeoutPromise: new Promise((_resolve, reject) => {
      timer = setTimeout(() => {
        const message =
          config.timeoutErrorMessage ?? `timeout of ${timeout}ms exceeded`;

        reject(createError(message, config, "ETIMEDOUT", request));
      }, timeout);
    }),
    timer,
  };
}

async function getResponsePromise(
  request: FetchAPIRequest,
  config: AxiosRequestConfig,
): Promise<(AxiosResponse & { ok: boolean }) | AxiosError> {
  let stageOne;
  try {
    stageOne = await fetch(request);
  } catch {
    return createError("Network Error", config, "ERR_NETWORK", request);
  }

  const headers: any = Object.assign({}, stageOne.headers as unknown);
  const response: AxiosResponse = {
    status: stageOne.status,
    statusText: stageOne.statusText,
    headers,
    config: {
      ...config,
      headers: config.headers as InternalAxiosRequestConfig["headers"],
    },
    request,
    data: undefined,
  };

  if (stageOne.status >= 200 && stageOne.status !== 204) {
    switch (config.responseType) {
      case "arraybuffer":
        response.data = await stageOne.arrayBuffer();
        break;
      case "blob":
        response.data = await stageOne.blob();
        break;
      case "json":
        response.data = await stageOne.json();
        break;
      default:
        response.data = await stageOne.text();
        break;
    }
  }

  return { ...response, ok: stageOne.ok };
}

function createRequest(config: AxiosRequestConfig): FetchAPIRequest {
  const headers = new Headers(config.headers as HeadersInit);

  // HTTP basic authentication
  if (config.auth) {
    const username = config.auth.username || "";
    const password = config.auth.password
      ? decodeURI(encodeURIComponent(config.auth.password))
      : "";

    const usernamePasswordBuffer = Buffer.from(`${username}:${password}`);

    headers.set(
      "Authorization",
      `Basic ${usernamePasswordBuffer}).toString("base64")}`,
    );
  }

  if (config.method === undefined) {
    config.method = "get";
  }

  const method = config.method.toUpperCase();
  let options: RequestInit = { headers, method };
  if (method !== "GET" && method !== "HEAD") {
    options.body = config.data;
  }

  // This config is similar to XHR’s withCredentials flag, but with three available values instead of two.
  // So if withCredentials is not set, default value 'same-origin' will be used
  if (config.withCredentials !== undefined) {
    options.credentials = config.withCredentials ? "include" : "omit";
  }

  const fullPath: string = buildFullPath(config.url ?? "", config.baseURL);

  const url: string = buildURL(
    fullPath,
    config.params,
    config.paramsSerializer,
  );

  if (import.meta.server) {
    options = getCloudflareCompatibleOptions(options);
  }

  return new Request(url, options);
}

// Replace all UUIDs in the URL with a placeholder
function replaceUuidsInUrl(url: string): string {
  return url.replace(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g,
    "*",
  );
}

function createError(
  message: string,
  config: AxiosRequestConfig,
  code: string,
  request: FetchAPIRequest,
  response?: AxiosResponse,
): AxiosError {
  const requestUrl = new URL(request.url);
  const errorMessageUrl = replaceUuidsInUrl(requestUrl.pathname);
  const errorMessage = `${request.method.toUpperCase()} ${errorMessageUrl} - ${message}`;

  const error = new AxiosError(errorMessage);

  error.config = {
    ...config,
    headers: config.headers as InternalAxiosRequestConfig["headers"],
  };

  error.code = code;
  error.request = request;
  error.response = response;

  error.toJSON = function toJSON() {
    return {
      // Standard
      message: this.message,
      name: this.name,
      stack: this.stack,
      // Axios
      config: this.config,
      code: this.code,
      status: this.response?.status ?? null,
    };
  };

  return error;
}

function getErrorCodeFromStatus(status: AxiosResponse["status"]): string {
  // 400 errors are bad requests, 500 errors are bad responses, and
  // everything else is probably a bad validation function
  return (
    ["ERR_BAD_REQUEST", "ERR_BAD_RESPONSE"][Math.floor(status / 100) - 4] ||
    "ERR_BAD_OPTION"
  );
}
